import { Box, BoxProps, Skeleton, Text } from "@mantine/core"
import classes from "./index.module.css"
import { RenderDate } from "./render-date"
import { SuspenseWithFallback } from "../../../../../-components/suspense"
import dayjs, { Dayjs } from "dayjs"
import { RenderCalendarProps } from ".."
import { useTranslation } from "react-i18next"

function getAllDaysOfWeeksOfMonth(month: Dayjs) {
  const startOfMonth = month.startOf("month")
  const endOfMonth = month.endOf("month")
  const startOfIsoWeek = startOfMonth.startOf("isoWeek")

  return Array.from({
    length: dayjs(endOfMonth).endOf("isoWeek").diff(startOfIsoWeek, "day") + 1,
  }).map((_, index) => startOfIsoWeek.add(index, "day"))
}

function CalendarHeaders({ month }: { month: Dayjs }) {
  useTranslation()

  return (
    <>
      <Text style={{ gridColumn: "span 7" }} fw={700}>
        {month.format("MMMM")}
      </Text>
      {Array.from({ length: 7 }).map((_, dayIndex) => (
        <Text key={dayIndex} fw={500}>
          {dayjs().startOf("isoWeek").add(dayIndex, "day").format("ddd")}
        </Text>
      ))}
    </>
  )
}

interface Props extends RenderCalendarProps, BoxProps {}

export const CalendarGrid = React.memo(
  ({
    startDate,
    endDate,
    eventsByDate,
    statusByDate,
    holidaysByDate,
    schoolHolidaysByDate,
    ...props
  }: Props) => {
    return (
      <Box className={classes["calendar-container"]} {...props}>
        <SuspenseWithFallback fallback={<Skeleton visible h="100px" />}>
          <Box
            style={{
              gap: "1rem",
              display: "grid",
              alignItems: "center",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              flexGrow: 1,
            }}
          >
            {Array.from({
              length: dayjs(endDate).diff(startDate, "month") + 1,
            }).map((_, monthIndex) => {
              const startOfMonth = dayjs(startDate)
                .startOf("month")
                .add(monthIndex, "month")

              const dayArray = getAllDaysOfWeeksOfMonth(startOfMonth)
              return (
                <Box className={classes["calendar-grid"]} key={monthIndex}>
                  <CalendarHeaders month={startOfMonth} />
                  {dayArray.map((dayToRender) => {
                    const dayToRenderString = dayToRender.format("YYYY-MM-DD")

                    const eventsForDate =
                      eventsByDate.get(dayToRenderString) || []
                    const statusForDate =
                      statusByDate.get(dayToRenderString) || []

                    const isSchoolHoliday =
                      schoolHolidaysByDate.has(dayToRenderString)

                    const isHoliday = holidaysByDate.has(dayToRenderString)

                    return (
                      <RenderDate
                        isSchoolHoliday={isSchoolHoliday}
                        isHoliday={isHoliday}
                        appointments={eventsForDate}
                        status={statusForDate}
                        key={`day-${dayToRenderString}`}
                        date={dayToRender.toDate()}
                        dimmed={dayToRender.month() !== startOfMonth.month()}
                      />
                    )
                  })}
                </Box>
              )
            })}
          </Box>
        </SuspenseWithFallback>
      </Box>
    )
  }
)

CalendarGrid.displayName = "CalendarGrid"
